import { useEffect, useState } from 'react';
import * as yup from 'yup';
// Ui
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
// Components
import Modal from 'ui3/Modal/Modal';
import Input from 'ui3/Input/Input';
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './InviteParticipantModal.module.scss';

export type InviteParticipantResult = {
  name: string;
  invitedEmail: string;
};

type InviteParticipantModalProps = {
  open: boolean;
  onClose: () => void;
  onInvite: (invitee: InviteParticipantResult) => void;
  initialValues?: InviteParticipantResult;
};

const INVALID_EMAIL_MESSAGE = 'Enter a valid email, e.g. name@example.com';
const NAME_REQUIRED_MESSAGE = 'Participant name is required';

const inviteEmailSchema = yup.string().trim().email(INVALID_EMAIL_MESSAGE);

const inviteParticipantSchema = yup.object().shape({
  name: yup.string().trim().required(NAME_REQUIRED_MESSAGE),
  invitedEmail: inviteEmailSchema.clone().required(INVALID_EMAIL_MESSAGE),
});

const getInviteEmailError = (value: string): string => {
  if (!value.trim()) {
    return '';
  }

  try {
    inviteEmailSchema.validateSync(value);
    return '';
  } catch (error) {
    return error instanceof yup.ValidationError
      ? error.message
      : INVALID_EMAIL_MESSAGE;
  }
};

/**
 * Collects the invitee's name and email for a matchup competitor slot. No
 * email is sent and no invite link exists yet: the backend generates the
 * invite token when the stream is created/updated and emails the /join link
 * only after those rows are committed — a link sent from this modal would
 * point at a competitor that doesn't exist yet.
 */
const InviteParticipantModal = ({
  open,
  onClose,
  onInvite,
  initialValues,
}: InviteParticipantModalProps): JSX.Element => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initialValues?.name ?? '');
      setEmail(initialValues?.invitedEmail ?? '');
      setEmailError(getInviteEmailError(initialValues?.invitedEmail ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validateEmail = (value: string) => {
    const nextError = getInviteEmailError(value);
    setEmailError(nextError);
    return !nextError;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Only re-check while an error is showing, so it clears as soon as the email is fixed.
    if (emailError) {
      validateEmail(value);
    }
  };

  const handleInvite = () => {
    try {
      const values = inviteParticipantSchema.validateSync(
        { name, invitedEmail: email },
        { abortEarly: false }
      ) as InviteParticipantResult;

      setEmailError('');
      onInvite({
        name: values.name,
        invitedEmail: values.invitedEmail,
      });
      onClose();
    } catch (error) {
      if (!(error instanceof yup.ValidationError)) {
        return;
      }

      const issues = error.inner.length > 0 ? error.inner : [error];
      const emailIssue = issues.find((issue) => issue.path === 'invitedEmail');
      const nameIssue = issues.find((issue) => issue.path === 'name');

      setEmailError(emailIssue?.message ?? '');

      if (nameIssue?.message) {
        showToast({
          type: 'error',
          message: nameIssue.message,
        });
      }
    }
  };

  const canInvite = Boolean(email.trim() && name.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initialValues ? 'Edit Participant' : 'Invite Participant'}
      size="small"
    >
      <div className={styles.root}>
        <Text
          className={styles.subtitle}
          variant="body2Regular"
          color="lights-medium"
        >
          Add a participant to your event. The invite will be emailed once the
          event is scheduled.
        </Text>

        <div className={styles.field}>
          <Input
            name="participantName"
            label="Participant Name"
            placeholder="Enter Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <Input
            name="invitedEmail"
            label="Invite via email"
            placeholder="Enter participant email"
            icon="mail"
            value={email}
            error={emailError}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={() => email.trim() && validateEmail(email)}
          />
        </div>

        <Button block disabled={!canInvite} onClick={handleInvite}>
          {initialValues ? 'Save Participant' : 'Add Participant'}
        </Button>
      </div>
    </Modal>
  );
};

export default InviteParticipantModal;
